/**
 * The commit timeline.
 *
 * Each row is headed by the commit's own name. The session scripts now derive
 * that name from the diff, so it says what changed; older commits predate that
 * and carry a fixed placeholder instead, and fall back to their computed
 * summary rather than repeating one string down the page.
 */

import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { type HistoryTimelineEntry, isPlaceholderCommitSubject } from '@shared/contentHistory'
import React from 'react'

interface HistoryTimelineProps {
  entries: HistoryTimelineEntry[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  /** Newest selected commit — the `to` side of the range. */
  selectedTo: string | null
  /** Oldest selected commit when a range is picked, else null. */
  selectedFrom: string | null
  /** Plain click selects one commit; shift-click extends to a range. */
  onSelect: (sha: string, extend: boolean) => void
  direction: 'ltr' | 'rtl'
}

const dayFormatter = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
const timeFormatter = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' })

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

function summaryLine(entry: HistoryTimelineEntry): string {
  const summary = entry.summary
  if (!summary) {
    return '—'
  }
  // Commits that changed nothing are filtered out upstream, so a row with no
  // sections can only be a bare chapter change — which this history has never
  // actually produced, but the label costs nothing.
  if (summary.sectionsChanged === 0) {
    return 'Structure only'
  }

  const parts: string[] = [`${summary.sectionsChanged} section${summary.sectionsChanged === 1 ? '' : 's'}`]
  if (summary.wordsAdded > 0 || summary.wordsRemoved > 0) {
    parts.push(`+${summary.wordsAdded} / −${summary.wordsRemoved} words`)
  } else {
    parts.push('moved or republished')
  }
  if (summary.sectionsAdded > 0) {
    parts.push(`${summary.sectionsAdded} new`)
  }
  if (summary.sectionsRemoved > 0) {
    parts.push(`${summary.sectionsRemoved} gone`)
  }
  return parts.join(' · ')
}

/** The commit's own name, or its computed summary when the name says nothing. */
function headline(entry: HistoryTimelineEntry): string {
  if (!isPlaceholderCommitSubject(entry.subject)) {
    return entry.subject
  }
  return summaryLine(entry)
}

export default function HistoryTimeline({
  entries,
  loading,
  hasMore,
  onLoadMore,
  selectedTo,
  selectedFrom,
  onSelect,
  direction,
}: HistoryTimelineProps) {
  const isRtl = direction === 'rtl'

  const selectedRange = React.useMemo(() => {
    if (!selectedTo) {
      return new Set<string>()
    }
    const toIndex = entries.findIndex((entry) => entry.sha === selectedTo)
    const fromIndex = selectedFrom ? entries.findIndex((entry) => entry.sha === selectedFrom) : toIndex
    if (toIndex === -1) {
      return new Set<string>()
    }
    const start = Math.min(toIndex, fromIndex === -1 ? toIndex : fromIndex)
    const end = Math.max(toIndex, fromIndex === -1 ? toIndex : fromIndex)
    return new Set(entries.slice(start, end + 1).map((entry) => entry.sha))
  }, [entries, selectedTo, selectedFrom])

  let lastDay = ''

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', px: 0.5, py: 1 }}>
      {entries.length === 0 && !loading && (
        <Typography sx={{ p: 2, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No history yet.</Typography>
      )}

      {entries.map((entry, index) => {
        const day = dayKey(entry.date)
        const showDay = day !== lastDay
        lastDay = day
        const selected = selectedRange.has(entry.sha)

        return (
          <React.Fragment key={entry.sha}>
            {showDay && (
              <Typography
                sx={{
                  px: 1.25,
                  pt: index === 0 ? 0 : 1.5,
                  pb: 0.5,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: 'var(--text-tertiary)',
                }}
              >
                {dayFormatter.format(new Date(entry.date))}
              </Typography>
            )}

            <Box
              onClick={(event) => onSelect(entry.sha, event.shiftKey)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 0.75,
                cursor: 'pointer',
                borderRadius: 1,
                userSelect: 'none',
                bgcolor: selected ? 'var(--sidebar-selected)' : 'transparent',
                // Selection rail on the reading-start edge, in both directions.
                [isRtl ? 'borderRight' : 'borderLeft']: '3px solid',
                borderColor: selected ? 'var(--diff-changed-bar)' : 'transparent',
                '&:hover': { bgcolor: selected ? 'var(--sidebar-selected)' : 'var(--bg-hover)' },
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  title={entry.subject}
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: selected ? 700 : 600,
                    color: selected ? 'var(--sidebar-selected-text)' : 'var(--text-primary)',
                    // Two lines: a generated name lists the sections it touched,
                    // which is worth reading in full.
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {headline(entry)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>
                    {timeFormatter.format(new Date(entry.date))}
                  </Typography>
                  {/* The metrics stay even when the name already lists them —
                      they are what the eye scans a timeline for. */}
                  {!isPlaceholderCommitSubject(entry.subject) && (
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        color: 'var(--text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {summaryLine(entry)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </React.Fragment>
        )
      })}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      )}

      {hasMore && !loading && (
        <Button size="small" onClick={onLoadMore} sx={{ mx: 1, mt: 1, fontSize: '0.72rem' }}>
          Load older
        </Button>
      )}
    </Box>
  )
}
